<template>
  <div class="card report-options flex flex-column align-item-start">
    <div class="report-options-text">
      <p>
        Сравнивает результаты со средним отраслевым ЭИ-профилем.
      </p>
      <p>
        Доступные сочетания для сравнения:
      </p>
      <p>
        только отрасль, только должность, только профессия, отрасль + должность,
        отрасль + профессия.
      </p>
    </div>

    <div class="report-options__body">
      <div class="report-options__field report-options__field--full">
        <div class="label">
          Группа
        </div>

        <a-select
            v-model:value="group"
            placeholder="Выберите группу"
            style="width: 100%;"
            not-found-content="Нет данных"
            show-arrow
        >
          <a-select-option
              v-for="item in groupOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div class="report-options__field report-options__field--full">
        <div class="label">
          Сочетание для сравнения
        </div>

        <a-select
            v-model:value="comparisonType"
            placeholder="Выберите сочетание"
            style="width: 100%;"
            not-found-content="Нет данных"
            show-arrow
        >
          <a-select-option
              v-for="item in comparisonOptions"
              :key="item.value"
              :value="item.value"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div
          v-if="showIndustrySelect"
          class="report-options__field"
      >
        <div class="label">
          Отрасль
        </div>

        <a-select
            v-model:value="industry"
            placeholder="Выберите отрасль"
            style="width: 100%;"
            not-found-content="Нет данных"
            show-arrow
        >
          <a-select-option
              v-for="item in industriesOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div
          v-if="showProfessionSelect"
          class="report-options__field"
      >
        <div class="label">
          Профессия
        </div>

        <a-select
            v-model:value="profession"
            placeholder="Выберите профессию"
            style="width: 100%;"
            not-found-content="Нет данных"
            show-arrow
        >
          <a-select-option
              v-for="item in professionOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div
          v-if="showPositionSelect"
          class="report-options__field"
      >
        <div class="label">
          Должность
        </div>

        <a-select
            v-model:value="position"
            placeholder="Выберите должность"
            style="width: 100%;"
            not-found-content="Нет данных"
            show-arrow
        >
          <a-select-option
              v-for="item in positionOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <div class="report-options__btn flex justify-content-center">
      <a-button
          class="primary-button"
          :disabled="!canGenerateReport"
          @click="requestForReport"
      >
        Сформировать отчет
      </a-button>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { getReportDetailsEmployeeCountText } from "./reportDetailsEmployeeCount";

export default {
  name: 'benchmark-parameters',

  setup(props, context) {
    const store = useStore();
    const router = useRouter();

    const group = ref(null);
    const comparisonType = ref(null);
    const industry = ref(null);
    const profession = ref(null);
    const position = ref(null);
    const lastGeneratedPayload = ref(null);

    const groupOptions = ref([]);
    const professionOptions = ref([]);
    const industriesOptions = ref([]);
    const positionOptions = ref([]);

    const reportIsLoading = ref(false)

    const selectedGroup = computed(() => store.state.analytics.selectedGroup);
    const groups = computed(() => store.state.group.groups);

    const industriesList = computed(() => store.state.test.industriesList);
    const professionList = computed(() => store.state.test.professionList);
    const positionList = computed(() => store.state.test.positionList);
    const user = computed(() => store.state.profile.profile);

    const comparisonOptions = [
      {
        text: 'Только отрасль',
        value: 'только отрасль',
      },
      {
        text: 'Только должность',
        value: 'только должность',
      },
      {
        text: 'Только профессия',
        value: 'только профессия',
      },
      {
        text: 'Отрасль + должность',
        value: 'отрасль + должность',
      },
      {
        text: 'Отрасль + профессия',
        value: 'отрасль + профессия',
      },
    ]

    const showIndustrySelect = computed(() => {
      return ['только отрасль', 'отрасль + должность', 'отрасль + профессия'].includes(comparisonType.value)
    })

    const showProfessionSelect = computed(() => {
      return ['только профессия', 'отрасль + профессия'].includes(comparisonType.value)
    })

    const showPositionSelect = computed(() => {
      return ['только должность', 'отрасль + должность'].includes(comparisonType.value)
    })

    const canFormReport = computed(() => {
      if (group.value === null || !comparisonType.value) {
        return false
      }

      if (showIndustrySelect.value && !industry.value) {
        return false
      }

      if (showProfessionSelect.value && !profession.value) {
        return false
      }

      if (showPositionSelect.value && !position.value) {
        return false
      }

      return true
    })

    onBeforeMount(() => {
      store.dispatch('group/getGroups').then(() => {
        groupOptions.value = [
          {
            id: 0,
            text: 'Все сотрудники',
            users_count: null,
          },
          ...groups.value.map((group) => ({
            id: group.id,
            text: group.name,
            users_count: group.users_count,
          })),
        ]
      })

      store.dispatch('test/getProfessionList').then(() => {
        professionOptions.value = professionList.value.map((profession) => ({
          id: profession.id,
          text: profession.text,
        }))
      })

      store.dispatch('test/getIndustriesList').then(() => {
        industriesOptions.value = industriesList.value.map((industry) => ({
          id: industry.id,
          text: industry.text,
        }))
      })

      store.dispatch('test/getPositionList').then(() => {
        positionOptions.value = positionList.value.map((position) => ({
          id: position.id,
          text: position.text,
        }))
      })
    })

    watch(comparisonType, () => {
      industry.value = null
      profession.value = null
      position.value = null
    })

    const getPayload = () => {
      return {
        company_id: user.value?.company?.id ?? null,
        group_id: group.value,
        combination: comparisonType.value,
        industry_id: showIndustrySelect.value ? industry.value : null,
        profession_id: showProfessionSelect.value ? profession.value : null,
        position_id: showPositionSelect.value ? position.value : null,
      }
    }

    const serializePayload = (payload) => {
      return JSON.stringify(payload)
    }

    const isReportAlreadyGenerated = computed(() => {
      if (!lastGeneratedPayload.value) {
        return false
      }

      return lastGeneratedPayload.value === serializePayload(getPayload())
    })

    const canGenerateReport = computed(() => {
      return canFormReport.value && !isReportAlreadyGenerated.value
    })

    const getTodayDateString = () => {
      const date = new Date();

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}.${month}.${year}`;
    }

    const selectedGroupObject = computed(() => {
      if (group.value === 0) {
        return {
          id: 0,
          text: 'Все сотрудники',
          users_count: null,
        }
      }

      const foundGroup = groups.value.find((item) => item.id === group.value);

      if (!foundGroup) {
        return {
          id: 0,
          text: 'Все сотрудники',
          users_count: null,
        }
      }

      return {
        id: foundGroup.id,
        text: foundGroup.name,
        users_count: foundGroup.users_count,
      }
    });

    const getSelectedGroupCount = (group) => {
      return group.id === 0 ? 'all' : group.users_count;
    };

    const selectedFilterObject = computed(() => {
      return comparisonOptions.find((item) => item.value === comparisonType.value) ?? null
    })

    const selectedFilterName = computed(() => {
      return selectedFilterObject.value?.text ?? null
    })

    const requestForReport = () => {
      if (!canFormReport.value) {
        return
      }

      const payload = getPayload()

      store.dispatch('group/clearAnalyticsGroupData')

      const currentGroup = selectedGroupObject.value

      const reportDetails = {
        groupName: currentGroup?.text ?? 'Все сотрудники',
        filterName: selectedFilterName.value,
        employeeCount: getReportDetailsEmployeeCountText(getSelectedGroupCount(currentGroup)),
        dateString: getTodayDateString(),
      }

      store.commit('analytics/setCurrentReportDetails', reportDetails)

      lastGeneratedPayload.value = serializePayload(payload)

      context.emit('reportRequest', payload)
    }

    onBeforeUnmount(() => {
      store.commit('analytics/clearCurrentReportDetails')
    })

    return {
      router,

      group,
      comparisonType,
      industry,
      profession,
      position,

      groupOptions,
      comparisonOptions,
      industriesOptions,
      professionOptions,
      positionOptions,

      selectedGroup,
      groups,

      selectedFilterObject,
      selectedFilterName,

      showIndustrySelect,
      showProfessionSelect,
      showPositionSelect,
      canFormReport,
      canGenerateReport,

      requestForReport,
      reportIsLoading,
    }
  },
}
</script>

<style scoped>
.report-options {
  gap: 20px;
}

.report-options-text {
  max-width: 800px;
  font-size: 14px;
  line-height: 1.35;
}

.report-options-text p {
  margin: 0;
}

.report-options__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: 660px;
  max-width: 100%;
}

.report-options__field {
  min-width: 0;
}

.report-options__field--full {
  grid-column: 1 / -1;
}

.label {
  margin-bottom: 6px;
  color: #707070;
  font-size: 14px;
  line-height: 1.2;
}

.report-options__btn {
  gap: 12px;
  margin-top: 4px;
}

@media (max-width: 768px) {
  .report-options__body {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .report-options__btn {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .report-options__btn .ant-btn {
    width: 100%;
  }
}
</style>
